import { Fragment, useState } from 'react';
import styles from './order.module.css';
import { Link, useParams } from 'react-router-dom';
import {
  useGenerateOrderItemDownloadMutation,
  useGetOrderQuery,
  useGetOrderSummaryQuery,
  useGetOrderTotalQuery,
  useUpdateOrderMutation,
} from './orders.api';
import { FormGroup } from 'shared-components';
import { toast } from 'react-toastify';
import Routes from '../../app/routes';
import { getShippingEmail, getShippingLabel } from './orders.types';
import { Currency } from '../currency/currency';
import {
  IContact,
  IEvent,
  IImage,
  IProduct,
  OrderStatus,
} from '@topshots/core-models';
import { DateTimeFormatter } from '../datetime/datetime';
import { Button } from 'shared-components';
import { ButtonTier } from 'shared-components';
import { open } from '../modal/modalSlice';
import { useAppDispatch } from '../../app/hooks';
import { Card, CardContent } from 'shared-components';
import { Label } from 'shared-components';
import { Input } from 'shared-components';
import { FormLayout } from 'shared-components';
import { PageTitle } from 'shared-components';

export const Order = () => {
  const dispatch = useAppDispatch();
  const [pollingInterval, setPollingInterval] = useState(0);
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useGetOrderQuery(id as string);
  const { data: totalData, isLoading: totalLoading } = useGetOrderTotalQuery(
    id as string
  );
  const { data: summaryData, isLoading: isSummaryLoading } =
    useGetOrderSummaryQuery(id as string, {
      pollingInterval,
    });
  const [update] = useUpdateOrderMutation();
  const [generate] = useGenerateOrderItemDownloadMutation();

  if (isLoading || isSummaryLoading || totalLoading) {
    return <span>loading...</span>;
  }

  const furtherActions = [];
  if (data?.isPaid) {
    furtherActions.push({ display: 'Send email', value: 'email' });
  }
  if (data?.status === OrderStatus.Confirmed) {
    furtherActions.push({ display: 'Mark as complete', value: 'complete' });
  } else {
    furtherActions.push({
      display: 'Apply a discount',
      value: 'discount',
    });
  }
  return (
    <>
      <PageTitle
        title="Order"
        crumbs={[
          { display: Routes.DASHBOARD.title, link: Routes.DASHBOARD.link },
          { display: Routes.ORDERS.title, link: Routes.ORDERS.link },
          {
            display: data?._id.toString() ?? '',
            link: Routes.ORDER.createLink?.(id as string) ?? '',
          },
        ]}
        furtherActions={furtherActions}
        onAction={async (action) => {
          switch (action) {
            case 'email':
              dispatch(
                open({
                  title: 'Send order confirmation email',
                  contentKey: 'send-order-email',
                  contentPayload: {
                    orderId: data?._id?.toString() ?? '',
                    initialEmail: (data?.contact_id as IContact).email ?? '',
                  },
                })
              );
              break;
            case 'discount':
              alert('not implemented');
              break;
            case 'complete':
              if (
                // eslint-disable-next-line no-restricted-globals
                confirm('Are you sure you want to mark this order as complete?')
              ) {
                await update({
                  id: id as string,
                  data: {
                    status: OrderStatus.Complete,
                  },
                });
                toast.success('Order updated!', {
                  autoClose: 2000,
                });
              }
              break;
          }
        }}
      />
      <Card>
        <CardContent>
          <FormLayout className={styles.order}>
            <div className={styles.address}>
              <Label>Ship to:</Label>
              <p
                dangerouslySetInnerHTML={{
                  __html: getShippingLabel(data),
                }}
              ></p>
            </div>
            <div className={styles.email}>
              <Label>Email to:</Label>
              <a href={`mailto:${getShippingEmail(data)}`}>
                {getShippingEmail(data)}
              </a>
            </div>
            <div className={styles.providerLinks}>
              {data?.paymentService === 'stripe' && (
                <a
                  href={`https://dashboard.stripe.com/payments/${data?.service_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View payment on Stripe
                </a>
              )}
            </div>
            <FormLayout className={styles.details}>
              <FormGroup>
                <Label htmlFor="status">Status</Label>
                <Input
                  disabled={true}
                  type="text"
                  id="status"
                  value={data?.status}
                />
              </FormGroup>
              {data?.isPaid && data?.paidOn && (
                <FormGroup>
                  <Label htmlFor="status">Paid on</Label>
                  <Input
                    disabled={true}
                    type="text"
                    id="status"
                    value={DateTimeFormatter.format(new Date(data.paidOn))}
                  />
                </FormGroup>
              )}
              {data?.isRefunded && data?.refundedOn && (
                <FormGroup>
                  <Label htmlFor="status">Refunded on</Label>
                  <Input
                    disabled={true}
                    type="text"
                    id="status"
                    value={DateTimeFormatter.format(new Date(data.refundedOn))}
                  />
                </FormGroup>
              )}
              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  disabled={true}
                  type="text"
                  id="email"
                  value={(data?.contact_id as IContact)?.email}
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  disabled={true}
                  type="text"
                  id="phone"
                  value={(data?.contact_id as IContact)?.phone}
                />
              </FormGroup>
            </FormLayout>
          </FormLayout>
        </CardContent>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Image</th>
              <th>Product</th>
              <th>Unit price</th>
              <th>Quantity</th>
              <th>Price</th>
            </tr>
          </thead>
          <tbody>
            {data?.items
              .filter((item) => (item.product_id as IProduct).addOnFor == null)
              .map((item, i) => {
                const downloadSummary = summaryData?.items.downloads.find(
                  (d) => d._id === item._id
                );
                const addOns = data?.items.filter((aItem) => {
                  const aImageIds = (aItem.image_ids as IImage[]).map((i) =>
                    i._id.toString()
                  );
                  const imageIds = (item.image_ids as IImage[]).map((i) =>
                    i._id.toString()
                  );
                  return (
                    (aItem.product_id as IProduct).addOnFor ===
                      (item.product_id as IProduct)._id.toString() &&
                    aImageIds.length === imageIds.length &&
                    aImageIds.every((id) => imageIds.includes(id))
                  );
                });
                return (
                  <Fragment key={i}>
                    <tr>
                      <td>
                        {item.image_ids.map((image, i) => (
                          <div
                            key={(image as IImage)._id.toString()}
                            className={styles.lineImage}
                          >
                            {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                            <img
                              style={{
                                maxWidth: '100px',
                              }}
                              src={
                                (image as IImage).isArchived
                                  ? (image as IImage).archiveUrl
                                  : (image as IImage).url
                              }
                              alt={(image as IImage).name}
                            />
                            <Link
                              to={
                                Routes.EVENTS +
                                '/' +
                                ((image as IImage).event_id as IEvent)._id
                              }
                            >
                              {((image as IImage).event_id as IEvent).name}
                            </Link>
                            <span>{(image as IImage).name}</span>
                          </div>
                        ))}
                      </td>
                      <td>
                        <Link
                          to={
                            Routes.PRODUCT.createLink?.(
                              (item.product_id as IProduct)._id.toString()
                            ) ?? ''
                          }
                        >
                          {(item.product_id as IProduct).name}
                        </Link>
                        {downloadSummary && (
                          <div>
                            <span>
                              Download status:{' '}
                              {downloadSummary.ready ? 'Ready' : 'Not Ready'}
                            </span>
                            {downloadSummary.ready ? (
                              <Button
                                tier={ButtonTier.Secondary}
                                onClick={() => {
                                  window.open(downloadSummary.downloadUrl);
                                  setPollingInterval(0);
                                }}
                              >
                                Download
                              </Button>
                            ) : (
                              <Button
                                tier={ButtonTier.Secondary}
                                onClick={async () => {
                                  await generate({
                                    id: data?._id?.toString() ?? '',
                                    itemId: item?._id?.toString() ?? '',
                                  });
                                  setPollingInterval(1000);
                                  toast.success(
                                    'Download generation requested!'
                                  );
                                }}
                              >
                                Generate
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        <Currency value={item.line_price / item.quantity} />
                      </td>
                      <td>x{item.quantity}</td>
                      <td>
                        <Currency value={item.line_price} />
                      </td>
                    </tr>
                    {(item.productFormData?.length ?? 0) > 0 && (
                      <tr>
                        <td>&nbsp;</td>
                        <td colSpan={4}>
                          <ul className={styles.formDataItem}>
                            <li className={styles.formDataItemProp}>
                              <span className={styles.formLabel}>Event</span>
                              <span className={styles.formValue}>
                                {(item.event_ids as IEvent[]).map((event) => (
                                  <Link
                                    to={
                                      Routes.EVENTS.link +
                                      '/' +
                                      event._id.toString()
                                    }
                                  >
                                    {event.name}
                                  </Link>
                                ))}
                              </span>
                            </li>
                          </ul>

                          {item.productFormData?.map((fieldMap, index) => (
                            <ul className={styles.formDataItem} key={index}>
                              {Object.entries(fieldMap).map(([key, value]) => (
                                <li
                                  key={index + key}
                                  className={styles.formDataItemProp}
                                >
                                  <span className={styles.formLabel}>
                                    {
                                      (
                                        item.product_id as IProduct
                                      ).formSchema.find((i) => i.id === key)
                                        ?.label
                                    }
                                  </span>
                                  <span className={styles.formValue}>
                                    {value}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ))}
                        </td>
                      </tr>
                    )}
                    {addOns.map((addOn, n) => {
                      const addOnDownloadSummary =
                        summaryData?.items.downloads.find(
                          (d) => d._id === item._id
                        );
                      return (
                        <tr key={`addOn_${i}_${n}`}>
                          <td>
                            <svg
                              style={{
                                height: 24,
                                width: 24,
                              }}
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="size-6"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M11.99 7.5 8.24 3.75m0 0L4.49 7.5m3.75-3.75v16.499h11.25"
                              />
                            </svg>
                            Add On
                          </td>
                          <td>
                            <Link
                              to={
                                Routes.PRODUCT.createLink?.(
                                  (addOn.product_id as IProduct)._id.toString()
                                ) ?? ''
                              }
                            >
                              {(addOn.product_id as IProduct).name}
                            </Link>
                            {addOnDownloadSummary && (
                              <div>
                                <span>
                                  Download status:{' '}
                                  {addOnDownloadSummary.ready
                                    ? 'Ready'
                                    : 'Not Ready'}
                                </span>
                                {addOnDownloadSummary.ready ? (
                                  <Button
                                    tier={ButtonTier.Secondary}
                                    onClick={() => {
                                      window.open(
                                        addOnDownloadSummary.downloadUrl
                                      );
                                      setPollingInterval(0);
                                    }}
                                  >
                                    Download
                                  </Button>
                                ) : (
                                  <Button
                                    tier={ButtonTier.Secondary}
                                    onClick={async () => {
                                      await generate({
                                        id: data?._id?.toString() ?? '',
                                        itemId: addOn?._id?.toString() ?? '',
                                      });
                                      setPollingInterval(1000);
                                      toast.success(
                                        'Download generation requested!'
                                      );
                                    }}
                                  >
                                    Generate
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                          <td>
                            <Currency
                              value={addOn.line_price / addOn.quantity}
                            />
                          </td>
                          <td>x{addOn.quantity}</td>
                          <td>
                            <Currency value={addOn.line_price} />
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              }) ?? null}
          </tbody>
          <tfoot>
            {data?.discounts.map((discount, i) => (
              <tr key={i}>
                <td className="text-right" colSpan={4}>
                  Discount: {discount.description}
                </td>
                <td>
                  <Currency value={discount.amount} />
                </td>
              </tr>
            ))}
            <tr>
              <td className="text-right" colSpan={4}>
                Shipping:
              </td>
              <td>
                <Currency value={data?.shippingCost ?? 0} />
              </td>
            </tr>
            <tr>
              <td className="text-right" colSpan={4}>
                Total:
              </td>
              <td>
                <Currency value={totalData?.total ?? 0} />
              </td>
            </tr>
          </tfoot>
        </table>
      </Card>
    </>
  );
};
